import {
  EXPANSION_TO_CONTRACTION_COUNT_RATIO,
  EXPANSION_VS_CONTRACTION_RATIO,
  GROSS_CHURN_COVERAGE,
  GROSS_TO_NET_SPREAD,
  GROWTH_EFFICIENCY_INDEX,
  GROWTH_QUICK_RATIO,
  RULE_OF_40,
} from "./exampleData";
import MetricBarChart from "./MetricBarChart";

const ratio = (v: number) => `${v}×`;
const pct = (v: number) => `${v}%`;
const pts = (v: number) => `${v}pp`;
// No label: the line sits at 1× with bars covering it full-width — the captions
// name the break-even threshold instead.
const breakEven = { y: 1 };

export const RuleOf40Chart = () => (
  <MetricBarChart
    caption="The Rule of 40 score — growth rate plus profit margin — climbs from 31 to 42 points, crossing the bar in June. A company can pass by growing fast, being profitable, or a balanced mix of both."
    data={RULE_OF_40}
    seriesName="Rule of 40 score"
    format={(v) => `${v}`}
    domain={[0, 50]}
    // Pin the ticks so the 40 threshold reads off the axis, aligned with the line.
    ticks={[0, 10, 20, 30, 40, 50]}
    reference={{ y: 40 }}
  />
);

export const GrowthQuickRatioChart = () => (
  <MetricBarChart
    caption="Growth quick ratio holds around 3–4× — dollars gained (new, expansion, reactivation) for every dollar lost to contraction and churn. Above 4× is elite; below 1× the book is shrinking."
    data={GROWTH_QUICK_RATIO}
    seriesName="Quick ratio"
    format={ratio}
    domain={[0, "dataMax + 1"]}
    reference={breakEven}
  />
);

export const GrossChurnCoverageChart = () => (
  <MetricBarChart
    caption="Gross churn coverage runs 2–3× — how many times new MRR covers the MRR lost to churn. Above 1× new business outpaces losses; below it, you're refilling a leaking bucket."
    data={GROSS_CHURN_COVERAGE}
    seriesName="Churn coverage"
    format={ratio}
    domain={[0, "dataMax + 1"]}
    reference={breakEven}
  />
);

export const ExpansionVsContractionRatioChart = () => (
  <MetricBarChart
    caption="Expansion-to-contraction MRR ratio swings between 1.4× and 4.8× — upsell dollars against downgrade dollars. Consistently above 1× means your existing base is trading up, not down."
    data={EXPANSION_VS_CONTRACTION_RATIO}
    seriesName="Exp : contraction"
    format={ratio}
    domain={[0, "dataMax + 1"]}
    reference={breakEven}
  />
);

export const ExpansionToContractionCountRatioChart = () => (
  <MetricBarChart
    caption="By customer count, expansions outnumber contractions 1.2–4.5× to one. Reading it beside the dollar ratio tells you whether a few big moves or many small ones are driving the trend."
    data={EXPANSION_TO_CONTRACTION_COUNT_RATIO}
    seriesName="Exp : contraction (count)"
    format={ratio}
    domain={[0, "dataMax + 1"]}
    reference={breakEven}
  />
);

export const GrowthEfficiencyIndexChart = () => (
  <MetricBarChart
    caption="Growth efficiency index sits near 70% — the share of gross positive movement that survives as net new MRR after churn and contraction. Higher means less of your growth is leaking back out."
    data={GROWTH_EFFICIENCY_INDEX}
    seriesName="Efficiency index"
    format={pct}
    domain={[0, 100]}
  />
);

export const GrossToNetSpreadChart = () => (
  <MetricBarChart
    caption="Gross-to-net spread is 7–16 points — the gap between NRR and GRR, which is exactly how much expansion is offsetting churn and contraction. A wide spread on a low GRR flags growth built on a leaky base."
    data={GROSS_TO_NET_SPREAD}
    seriesName="NRR − GRR"
    format={pts}
    domain={[0, "dataMax + 5"]}
  />
);
